/**
 * Reclaim excess rent from the wallet's token accounts without closing them.
 *
 * See `src/lib/solana/excessRent.ts` for the on-chain mechanics. This hook owns
 * the scan (react-query) and the sign-and-send flow across the wallet providers
 * the app supports (Phantom embedded/extension, AppKit/WalletConnect, injected).
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Connection,
  PublicKey,
  Transaction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { getConnection, PROGRAM_ID } from '@/lib/solana/anchorClient';
import {
  scanExcessRent,
  buildWithdrawExcessTransactions,
  getCreditsAccountStatus,
  groupWithdrawBatches,
  withoutAccounts,
  type ExcessRentAccount,
  type ExcessRentScan,
} from '@/lib/solana/excessRent';
import { formatLamportsToSol } from '@/utils/solana/lamportsToSol';
import { logger, getGenericErrorMessage } from '@/lib/logger';
import { captureException, createErrorContext } from '@/lib/sentry';
import { parseSolanaError, isUserActionableError } from '@/utils/solanaErrorParser';

export type ReclaimExcessRentStep = 'signing' | 'sending' | 'confirming' | 'complete';

export interface ReclaimExcessRentProgress {
  step: ReclaimExcessRentStep;
  currentIndex: number;
  totalCount: number;
}

export interface ReclaimExcessRentInput {
  scan: ExcessRentScan;
  /** Deposit the reclaimed SOL into the user's Junk.fun credits instead of leaving it in the wallet */
  depositToCredits: boolean;
}

export interface ReclaimExcessRentResult {
  signatures: string[];
  accountsReclaimed: number;
  /** Lamports the scan expected to recover (the chain moves exactly the excess) */
  reclaimedLamports: number;
  /** True when the SOL went into Junk.fun credits rather than the wallet */
  depositedToCredits: boolean;
}

export const EXCESS_RENT_QUERY_KEY = 'excessRent';
export const CREDITS_ACCOUNT_QUERY_KEY = 'creditsAccount';

/**
 * Accounts drained by a batch that has landed, with the time they stop being hidden.
 * Load-balanced RPC nodes can lag the chain by a few seconds, so a scan run right
 * after confirmation may still report the old balances. Rather than trust that first
 * re-read, hide the drained accounts for a window far longer than any realistic lag.
 */
const RECLAIMED_HIDE_MS = 90_000;
const recentlyReclaimed = new Map<string, number>();

function markReclaimed(accounts: ExcessRentAccount[]): void {
  const hideUntil = Date.now() + RECLAIMED_HIDE_MS;
  for (const account of accounts) recentlyReclaimed.set(account.address, hideUntil);
}

/** Scan as the panel should show it: minus accounts we know are already drained */
export function hideRecentlyReclaimed(scan: ExcessRentScan): ExcessRentScan {
  const now = Date.now();
  for (const [address, hideUntil] of recentlyReclaimed) {
    if (hideUntil <= now) recentlyReclaimed.delete(address);
  }
  return withoutAccounts(scan, new Set(recentlyReclaimed.keys()));
}

/** Flip the panel now from what has landed, then re-read as the RPC catches up */
function settleScan(queryClient: QueryClient, address: string | null | undefined): void {
  const queryKey = [EXCESS_RENT_QUERY_KEY, address];
  queryClient.setQueryData<ExcessRentScan>(queryKey, (old) => (old ? hideRecentlyReclaimed(old) : old));
  for (const delayMs of [2_500, 8_000]) {
    setTimeout(() => void queryClient.invalidateQueries({ queryKey }), delayMs);
  }
}

async function confirmSignature(
  connection: Connection,
  signature: string,
  blockhash: string,
  lastValidBlockHeight: number
): Promise<void> {
  const result = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'confirmed'
  );
  if (result.value.err) {
    throw new Error(`Transaction failed: ${JSON.stringify(result.value.err)}`);
  }
}

async function reclaimExcessRent(
  { scan, depositToCredits }: ReclaimExcessRentInput,
  walletProvider: any,
  walletAddress: string,
  onProgress?: (progress: ReclaimExcessRentProgress) => void
): Promise<ReclaimExcessRentResult> {
  const errorContext = createErrorContext('reclaim_excess_rent', walletAddress, {
    accounts: scan.accounts.length,
    totalExcessLamports: scan.totalExcessLamports,
    depositToCredits,
    isPhantom: walletProvider?.isPhantom === true,
  });

  if (scan.accounts.length === 0) {
    throw new Error('No excess rent to reclaim');
  }

  const connection = getConnection();
  const owner = new PublicKey(walletAddress);
  const transactions = buildWithdrawExcessTransactions(scan.accounts, owner, {
    creditsProgramId: depositToCredits ? PROGRAM_ID : undefined,
  });
  // Same grouping and order as the transactions, so batches[i] is what transactions[i] drains
  const batches = groupWithdrawBatches(scan.accounts);
  const totalCount = transactions.length;
  const signatures: string[] = [];

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  for (const tx of transactions) {
    tx.feePayer = owner;
    tx.recentBlockhash = blockhash;
    // Some injected wallets need the signer list populated before they enable "Sign"
    tx.setSigners(owner);
  }

  const isPhantomWallet = walletProvider.isPhantom === true;

  try {
    if (isPhantomWallet && typeof walletProvider.signAndSendTransaction === 'function') {
      // Phantom (embedded wallets only support signAndSendTransaction)
      for (let i = 0; i < totalCount; i++) {
        onProgress?.({ step: 'signing', currentIndex: i, totalCount });
        const messageV0 = new TransactionMessage({
          payerKey: owner,
          recentBlockhash: blockhash,
          instructions: transactions[i].instructions,
        }).compileToV0Message();
        const { signature } = await walletProvider.signAndSendTransaction(new VersionedTransaction(messageV0));
        onProgress?.({ step: 'confirming', currentIndex: i, totalCount });
        await confirmSignature(connection, signature, blockhash, lastValidBlockHeight);
        markReclaimed(batches[i]);
        signatures.push(signature);
      }
    } else if (typeof walletProvider.sendTransaction === 'function') {
      // AppKit / WalletConnect providers require sendTransaction
      for (let i = 0; i < totalCount; i++) {
        onProgress?.({ step: 'signing', currentIndex: i, totalCount });
        const signature: string = await walletProvider.sendTransaction(transactions[i] as any, connection as any, {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
          maxRetries: 3,
        });
        onProgress?.({ step: 'confirming', currentIndex: i, totalCount });
        await confirmSignature(connection, signature, blockhash, lastValidBlockHeight);
        markReclaimed(batches[i]);
        signatures.push(signature);
      }
    } else {
      // Injected wallets: one approval for all batches when supported
      onProgress?.({ step: 'signing', currentIndex: 0, totalCount });
      let signed: Transaction[];
      if (typeof walletProvider.signAllTransactions === 'function') {
        signed = await walletProvider.signAllTransactions(transactions);
      } else {
        signed = [];
        for (const tx of transactions) {
          signed.push(await walletProvider.signTransaction(tx));
        }
      }

      for (let i = 0; i < signed.length; i++) {
        onProgress?.({ step: 'sending', currentIndex: i, totalCount });
        const signature = await connection.sendRawTransaction(signed[i].serialize(), {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
          maxRetries: 3,
        });
        onProgress?.({ step: 'confirming', currentIndex: i, totalCount });
        await confirmSignature(connection, signature, blockhash, lastValidBlockHeight);
        markReclaimed(batches[i]);
        signatures.push(signature);
      }
    }
  } catch (error) {
    // Report how far we got; batches that already landed have moved real SOL
    captureException(error as Error, {
      ...errorContext,
      stage: 'reclaim_excess_rent_send',
      completedBatches: signatures.length,
      totalBatches: totalCount,
    });
    if (signatures.length > 0) {
      const completedAccounts = Math.min(scan.accounts.length, signatures.length * (scan.accounts.length / totalCount));
      logger.warn(`[RECLAIM-RENT] ${signatures.length}/${totalCount} batches landed before the error (~${Math.floor(completedAccounts)} accounts)`);
    }
    throw error;
  }

  onProgress?.({ step: 'complete', currentIndex: totalCount, totalCount });
  logger.log(
    `[RECLAIM-RENT] Reclaimed excess from ${scan.accounts.length} accounts in ${totalCount} tx(s)` +
      (depositToCredits ? ', deposited into Junk.fun credits' : '')
  );

  return {
    signatures,
    accountsReclaimed: scan.accounts.length,
    reclaimedLamports: scan.totalExcessLamports,
    depositedToCredits: depositToCredits,
  };
}

/** Whether the wallet's Junk.fun credits PDA exists, and the one-time rent a first deposit would cost */
export function useCreditsAccountStatus() {
  const { address, isConnected } = useWalletAddress();

  return useQuery({
    queryKey: [CREDITS_ACCOUNT_QUERY_KEY, address],
    queryFn: () => getCreditsAccountStatus(getConnection(), new PublicKey(address!), PROGRAM_ID),
    enabled: isConnected && !!address,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useExcessRentScan() {
  const { address, isConnected } = useWalletAddress();

  return useQuery({
    queryKey: [EXCESS_RENT_QUERY_KEY, address],
    queryFn: () => scanExcessRent(getConnection(), new PublicKey(address!)),
    enabled: isConnected && !!address,
    select: hideRecentlyReclaimed,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useReclaimExcessRent(options?: {
  onProgress?: (progress: ReclaimExcessRentProgress) => void;
  onReclaimed?: (result: ReclaimExcessRentResult) => void;
}) {
  const { address, isConnected, walletProvider } = useWalletAddress();
  const queryClient = useQueryClient();

  // Credits landed on-chain (fully or from the batches that made it), so the chest
  // credits and the PDA-exists check are stale until re-read
  const refreshCredits = () => {
    queryClient.invalidateQueries({ queryKey: ['userProgress', address] });
    queryClient.invalidateQueries({ queryKey: [CREDITS_ACCOUNT_QUERY_KEY, address] });
  };

  return useMutation({
    mutationFn: async (input: ReclaimExcessRentInput) => {
      if (!isConnected || !address) {
        throw new Error('Wallet not connected');
      }
      if (!walletProvider) {
        throw new Error('Wallet provider not available');
      }
      return reclaimExcessRent(input, walletProvider, address, options?.onProgress);
    },
    onSuccess: (result) => {
      const amount = formatLamportsToSol(BigInt(result.reclaimedLamports), 6);
      const from = `${result.accountsReclaimed} token account${result.accountsReclaimed === 1 ? '' : 's'}`;
      toast.success(
        result.depositedToCredits
          ? `Reclaimed ${amount} SOL of excess rent from ${from} into your Junk.Fun credits`
          : `Reclaimed ${amount} SOL of excess rent from ${from}`
      );
      settleScan(queryClient, address);
      if (result.depositedToCredits) refreshCredits();
      options?.onReclaimed?.(result);
    },
    onError: (error: Error, input) => {
      logger.error('[RECLAIM-RENT] error:', error);
      const parsedError = parseSolanaError(error);
      if (isUserActionableError(parsedError)) {
        toast.error('Reclaim Failed', {
          description: parsedError.suggestedAction
            ? `${parsedError.message}\n\n${parsedError.suggestedAction}`
            : parsedError.message,
          duration: 8000,
        });
      } else {
        toast.error(getGenericErrorMessage(error));
      }
      // Partial success is possible: batches that landed are already marked, show what is left
      settleScan(queryClient, address);
      if (input.depositToCredits) refreshCredits();
    },
  });
}
